import React from "react";
import Layout from "../components/Layout";
import { Link } from "react-router-dom";

const LAST_UPDATED = "18 de febrero de 2026";

export default function Privacy() {
  return (
    <Layout>
      <div className="max-w-[820px] mx-auto px-5 py-8 text-[#c7d0e1]" data-testid="privacy-page">
        <h1 className="font-pixel text-[16px] text-glow-green mb-2">◆ PRIVACY POLICY</h1>
        <p className="text-[11px] text-[#8b95a9] mb-6">Última actualización: {LAST_UPDATED}</p>

        <p className="mb-5 text-[13px] leading-relaxed">
          En <strong>CRYPTOBOOM</strong> tratamos tus datos con transparencia. Esta política describe
          qué recopilamos, para qué, con quién lo compartimos y qué derechos tienes. Cumplimos con
          los principios del RGPD (UE) y la LFPDPPP (México), donde aplique.
        </p>

        <Section title="1. Datos que recopilamos">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Cuenta</strong>: email, username, hash de contraseña (nunca la contraseña en claro), fecha de creación, rol.</li>
            <li><strong>Gameplay</strong>: puntuaciones, distancia, bloques destruidos, coins, sesiones de juego, streak, historial de partidas.</li>
            <li><strong>Economía</strong>: saldo BC, transacciones, compras del shop, canjes a UNI2, cosméticos poseídos, power-ups.</li>
            <li><strong>Social</strong>: código de referral, quién te invitó, tareas sociales completadas.</li>
            <li><strong>Preferencias</strong>: idioma, SFX on/off.</li>
            <li><strong>Técnicos</strong>: IP, User-Agent, hora de acceso, cookies estrictamente necesarias.</li>
            <li><strong>Analytics</strong> (si aplicable): eventos anónimos de producto vía PostHog / Google Analytics.</li>
          </ul>
        </Section>

        <Section title="2. Para qué usamos tus datos">
          <ul className="list-disc pl-5 space-y-1">
            <li>Operar el Servicio: autenticación, guardar tu progreso, procesar recompensas.</li>
            <li>Prevenir fraude, bots y abuso (anti-cheat).</li>
            <li>Comunicarnos contigo sobre tu cuenta, incidencias o cambios en el Servicio.</li>
            <li>Mejorar el producto mediante métricas agregadas y anónimas.</li>
            <li>Cumplir obligaciones legales y responder a requerimientos de autoridades competentes.</li>
          </ul>
        </Section>

        <Section title="3. Base legal (RGPD)">
          <p>El tratamiento se basa en: <strong>ejecución del contrato</strong> (Términos), <strong>consentimiento</strong> (analytics, comunicaciones opcionales), <strong>interés legítimo</strong> (fraude, seguridad) y <strong>obligación legal</strong> (cuando aplique).</p>
        </Section>

        <Section title="4. Con quién compartimos tus datos">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Proveedores de infraestructura</strong>: hosting, base de datos, CDN. Bajo acuerdos de tratamiento de datos.</li>
            <li><strong>Proveedores de analytics</strong> (PostHog, Google Analytics) — sólo eventos anónimos.</li>
            <li><strong>Blockchain UNI2Chain</strong>: las conversiones a UNI2 y las direcciones on-chain son públicas por naturaleza. No publicamos tu email en la blockchain.</li>
            <li><strong>Autoridades</strong>: sólo bajo requerimiento legal válido.</li>
          </ul>
          <p className="mt-2">No vendemos tus datos personales a terceros.</p>
        </Section>

        <Section title="5. Cookies y almacenamiento local">
          Usamos localStorage y cookies estrictamente necesarias para: mantener tu sesión (JWT), preferencia de idioma (<code>cb_locale</code>), preferencia de audio (<code>cb_sfx</code>) y código de referral pendiente (<code>cb_ref_code</code>, TTL 30 días). No usamos cookies publicitarias de terceros.
        </Section>

        <Section title="6. Retención de datos">
          <ul className="list-disc pl-5 space-y-1">
            <li>Cuenta: hasta que la elimines o pasen 24 meses de inactividad continua.</li>
            <li>Historial de partidas: 24 meses.</li>
            <li>Transacciones: 5 años (obligación fiscal/legal).</li>
            <li>Logs técnicos: 90 días.</li>
          </ul>
          Puedes solicitar el borrado de tu cuenta escribiendo a soporte (ver §9). Ciertos registros mínimos pueden retenerse por ley.
        </Section>

        <Section title="7. Seguridad">
          Almacenamos contraseñas con <strong>bcrypt</strong>, servimos todo el tráfico por HTTPS, validamos JWT en cada petición, y aplicamos rate limiting y detección de anomalías. Ninguna medida es infalible; te recomendamos usar una contraseña única.
        </Section>

        <Section title="8. Menores">
          El Servicio no está dirigido a menores de 13 años. Si eres el tutor legal de un menor que
          ha creado una cuenta, contáctanos para borrarla.
        </Section>

        <Section title="9. Tus derechos">
          Tienes derecho a acceder, rectificar, borrar, oponerte al tratamiento, limitar el
          tratamiento, portabilidad y retirar el consentimiento en cualquier momento. Para ejercerlos,
          escribe a <a href="mailto:privacy@cryptoboom.io" className="text-[#2ecc71]">privacy@cryptoboom.io</a>. Responderemos en un plazo máximo de 30 días.
        </Section>

        <Section title="10. Transferencias internacionales">
          Nuestros proveedores pueden procesar datos fuera de tu país. En esos casos, aplicamos
          cláusulas contractuales tipo o mecanismos equivalentes de garantía.
        </Section>

        <Section title="11. Cambios en esta política">
          Podemos actualizar esta Política. Publicaremos la fecha de "Última actualización" al inicio.
          Los cambios significativos se anunciarán dentro del Servicio.
        </Section>

        <Section title="12. Contacto y autoridad de control">
          Escríbenos a <a href="mailto:privacy@cryptoboom.io" className="text-[#2ecc71]">privacy@cryptoboom.io</a>. Si consideras que no atendemos tu solicitud, puedes acudir a la
          autoridad de protección de datos competente en tu jurisdicción.
        </Section>

        <p className="mt-8 text-[11px] text-[#8b95a9]">
          Ver también nuestros <Link to="/terms" className="text-[#2ecc71]">Términos de Servicio</Link>.
        </p>
      </div>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-5">
      <h2 className="font-pixel text-[10px] text-[#f7931a] mb-2 tracking-wide">{title}</h2>
      <div className="text-[13px] leading-relaxed text-[#c7d0e1]">{children}</div>
    </section>
  );
}
